import React, { useState } from "react";
import { Mail, Plus, Pencil, ArrowRight } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../../components/ui/select";
import CatalogTable from "../components/CatalogTable";
import { CatalogLoadingCard, CatalogErrorCard } from "../components/CatalogStateCard";
import EntityFormDialog from "../components/EntityFormDialog";
import MutationErrorAlert from "../components/MutationErrorAlert";
import StatusBadge from "../components/StatusBadge";
import { useEmailCatalogEntityList } from "../../../hooks/useEmailCatalogEntityList";
import { getSenderConfigurations, createSenderConfiguration, updateSenderConfiguration, describeMutationError } from "../../../lib/emailMarketingApi";
import { BROADCAST_ENVIRONMENT_VALUES } from "../broadcasts/broadcastStatusTransitions";

/**
 * SenderConfigurationsPanel — EMAILS-IMPL-021
 *
 * Governed Sender Configuration authoring (BRD-SPEC-001 §10.2): browse
 * the tenant-wide, Environment-scoped catalog, create a Draft, edit a
 * Draft's fields freely, Approve (freezing name/environment/from_name/
 * from_address/reply_to permanently), or Retire an Approved record
 * (terminal). No edit affordance is ever offered once a record leaves
 * Draft -- the backend enforces this independently regardless.
 */

function emptyCreateForm() {
  return { name: "", environment: "", fromName: "", fromAddress: "", replyTo: "" };
}

export default function SenderConfigurationsPanel() {
  const listFn = React.useCallback(() => getSenderConfigurations(), []);
  const createFn = React.useCallback(
    ({ name, environment, fromName, fromAddress, replyTo }) => createSenderConfiguration({ name, environment, fromName, fromAddress, replyTo }),
    []
  );
  const updateFn = React.useCallback((id, payload) => updateSenderConfiguration(id, payload), []);
  const getIdFn = React.useCallback((row) => row.id, []);

  const { items, loading, error, mutating, mutationError, clearMutationError, createItem, updateItem } = useEmailCatalogEntityList({
    list: listFn, create: createFn, update: updateFn, getId: getIdFn,
  });

  const [expandedId, setExpandedId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreateForm());
  const [editingSender, setEditingSender] = useState(null);
  const [editForm, setEditForm] = useState(emptyCreateForm());

  // Lifecycle-transition actions (Approve/Retire) get their own error
  // surface, distinct from the Create/Edit dialogs' own mutationError --
  // they are not dialog-form submissions.
  const [transitionError, setTransitionError] = useState(null);

  const openCreate = () => {
    setCreateForm(emptyCreateForm());
    clearMutationError();
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    try {
      await createItem({
        name: createForm.name.trim(),
        environment: createForm.environment,
        fromName: createForm.fromName.trim(),
        fromAddress: createForm.fromAddress.trim(),
        replyTo: createForm.replyTo.trim(),
      });
      setCreateOpen(false);
    } catch {
      // handled via mutationError
    }
  };

  const openEdit = (sender) => {
    setEditingSender(sender);
    setEditForm({
      name: sender.name, environment: sender.environment,
      fromName: sender.from_name, fromAddress: sender.from_address, replyTo: sender.reply_to,
    });
    clearMutationError();
  };

  const submitEdit = async () => {
    try {
      await updateItem(editingSender.id, {
        name: editForm.name.trim() !== editingSender.name ? editForm.name.trim() : undefined,
        environment: editForm.environment !== editingSender.environment ? editForm.environment : undefined,
        fromName: editForm.fromName.trim() !== editingSender.from_name ? editForm.fromName.trim() : undefined,
        fromAddress: editForm.fromAddress.trim() !== editingSender.from_address ? editForm.fromAddress.trim() : undefined,
        replyTo: editForm.replyTo.trim() !== editingSender.reply_to ? editForm.replyTo.trim() : undefined,
      });
      setEditingSender(null);
    } catch {
      // handled via mutationError
    }
  };

  const transition = async (senderConfigurationId, status) => {
    setTransitionError(null);
    try {
      await updateItem(senderConfigurationId, { status });
    } catch (err) {
      setTransitionError(describeMutationError(err, "Failed to update status."));
    }
  };

  if (loading) return <CatalogLoadingCard testId="senders-loading" />;
  if (error) return <CatalogErrorCard message={error} testId="senders-error" />;

  return (
    <div className="space-y-4" data-testid="senders-panel">
      <CatalogTable
        testId="senders-table"
        title="Sender Configurations"
        icon={Mail}
        headerAction={
          <Button
            type="button"
            size="sm"
            className="gap-1.5 bg-violet-600 text-white hover:bg-violet-500"
            onClick={openCreate}
            data-testid="new-sender-button"
          >
            <Plus className="h-4 w-4" /> New Sender Configuration
          </Button>
        }
        rows={items}
        getRowKey={(row) => row.id}
        onRowClick={(row) => setExpandedId(expandedId === row.id ? null : row.id)}
        emptyTitle="No Sender Configurations yet"
        emptySubtitle='Create one with "New Sender Configuration" to get started.'
        columns={[
          {
            key: "name",
            label: "Name",
            render: (row) => <span className="text-sm font-medium text-slate-100">{row.name}</span>,
          },
          {
            key: "environment",
            label: "Environment",
            render: (row) => (
              <span
                className={`text-xs font-semibold uppercase tracking-wide ${row.environment === "Production" ? "text-rose-300" : "text-sky-300"}`}
                data-testid={`sender-environment-${row.id}`}
              >
                {row.environment}
              </span>
            ),
          },
          {
            key: "from",
            label: "From",
            render: (row) => (
              <div>
                <div className="text-sm text-slate-200">{row.from_name}</div>
                <div className="text-xs text-slate-400">{row.from_address}</div>
              </div>
            ),
          },
          { key: "reply_to", label: "Reply-To", render: (row) => <span className="text-sm text-slate-300">{row.reply_to}</span> },
          { key: "status", label: "Status", render: (row) => <StatusBadge status={row.status} testId={`sender-status-${row.id}`} /> },
        ]}
      />

      {items
        .filter((row) => row.id === expandedId)
        .map((row) => (
          <div key={row.id} className="space-y-3 rounded-lg border border-white/10 bg-white/5 p-4" data-testid={`sender-detail-${row.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
                <Detail label="Name" value={row.name} />
                <Detail label="Environment" value={row.environment} />
                <Detail label="Status" value={<StatusBadge status={row.status} testId={`sender-detail-status-${row.id}`} />} />
                <Detail label="From Name" value={row.from_name} />
                <Detail label="From Address" value={row.from_address} mono />
                <Detail label="Reply-To" value={row.reply_to} mono />
              </div>
              {row.status === "Draft" && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="gap-1.5 border-white/10 text-slate-200"
                  onClick={() => openEdit(row)}
                  data-testid={`edit-sender-${row.id}`}
                >
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </Button>
              )}
            </div>

            <div className="flex flex-wrap gap-2 border-t border-white/10 pt-3">
              {row.status === "Draft" && (
                <Button
                  type="button"
                  size="sm"
                  className="gap-1.5 bg-emerald-600 text-white hover:bg-emerald-500"
                  disabled={mutating}
                  onClick={() => transition(row.id, "Approved")}
                  data-testid={`approve-sender-${row.id}`}
                >
                  <ArrowRight className="h-3.5 w-3.5" /> Approve
                </Button>
              )}
              {row.status === "Approved" && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="gap-1.5 border-amber-400/30 text-amber-300"
                  disabled={mutating}
                  onClick={() => transition(row.id, "Retired")}
                  data-testid={`retire-sender-${row.id}`}
                >
                  <ArrowRight className="h-3.5 w-3.5" /> Retire
                </Button>
              )}
              {row.status === "Retired" && (
                <p className="text-xs text-slate-500" data-testid={`sender-retired-note-${row.id}`}>
                  Retired — no further changes possible. Create a new Sender Configuration to change sender identity.
                </p>
              )}
            </div>
            {transitionError && <MutationErrorAlert error={transitionError} testId={`sender-transition-error-${row.id}`} />}
          </div>
        ))}

      <EntityFormDialog
        testId="create-sender-dialog"
        open={createOpen}
        onOpenChange={setCreateOpen}
        title="New Sender Configuration"
        description="Governed sender identity, scoped to one Broadcast Environment. Operators select an Approved configuration on a Broadcast — never type sender values directly."
        onSubmit={submitCreate}
        submitting={mutating}
        error={mutationError}
        submitLabel="Create Sender Configuration"
      >
        <SenderFields form={createForm} setForm={setCreateForm} testPrefix="sender" />
      </EntityFormDialog>

      {editingSender && (
        <EntityFormDialog
          testId="edit-sender-dialog"
          open={!!editingSender}
          onOpenChange={(open) => !open && setEditingSender(null)}
          title={`Edit ${editingSender.name}`}
          description="Draft configurations may be edited freely. Approving freezes every field permanently."
          onSubmit={submitEdit}
          submitting={mutating}
          error={mutationError}
          submitLabel="Save Changes"
        >
          <SenderFields form={editForm} setForm={setEditForm} testPrefix="sender-edit" />
        </EntityFormDialog>
      )}
    </div>
  );
}

function SenderFields({ form, setForm, testPrefix }) {
  return (
    <>
      <div className="space-y-2">
        <Label htmlFor={`${testPrefix}-name`}>Name</Label>
        <Input
          id={`${testPrefix}-name`}
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          placeholder="e.g. Primary iLifeQuotes Sender"
          required
          data-testid={`${testPrefix}-name-input`}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${testPrefix}-environment-select`}>Environment</Label>
        <Select value={form.environment} onValueChange={(v) => setForm((f) => ({ ...f, environment: v }))}>
          <SelectTrigger id={`${testPrefix}-environment-select`} data-testid={`${testPrefix}-environment-select`}>
            <SelectValue placeholder="Select an Environment" />
          </SelectTrigger>
          <SelectContent>
            {BROADCAST_ENVIRONMENT_VALUES.map((env) => (
              <SelectItem key={env} value={env} data-testid={`${testPrefix}-environment-option-${env}`}>
                {env}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${testPrefix}-from-name`}>From Name</Label>
        <Input
          id={`${testPrefix}-from-name`}
          value={form.fromName}
          onChange={(e) => setForm((f) => ({ ...f, fromName: e.target.value }))}
          placeholder="e.g. iLifeQuotes"
          required
          data-testid={`${testPrefix}-from-name-input`}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${testPrefix}-from-address`}>From Address</Label>
        <Input
          id={`${testPrefix}-from-address`}
          type="email"
          value={form.fromAddress}
          onChange={(e) => setForm((f) => ({ ...f, fromAddress: e.target.value }))}
          placeholder="e.g. hello@ilifequotes.com"
          required
          data-testid={`${testPrefix}-from-address-input`}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${testPrefix}-reply-to`}>Reply-To</Label>
        <Input
          id={`${testPrefix}-reply-to`}
          type="email"
          value={form.replyTo}
          onChange={(e) => setForm((f) => ({ ...f, replyTo: e.target.value }))}
          placeholder="e.g. support@ilifequotes.com"
          required
          data-testid={`${testPrefix}-reply-to-input`}
        />
      </div>
    </>
  );
}

function Detail({ label, value, mono }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={mono ? "font-mono text-slate-200" : "text-slate-200"}>{value}</div>
    </div>
  );
}
